<template>
  <div class="flex flex-center q-pb-none">
    <q-btn-group unelevated dense>
      <q-btn
        unelevated
        label="No Schedule"
        no-caps
        :text-color="scheduleFrequency === null ? 'primary' : $q.dark.isActive ? 'white' : 'black'"
        @click="setDefaultSchedule(null)"
      />
      <q-btn
        unelevated
        label="Daily"
        no-caps
        :text-color="
          scheduleFrequency === 'daily' ? 'primary' : $q.dark.isActive ? 'white' : 'black'
        "
        @click="setDefaultSchedule('daily')"
      />
      <q-btn
        unelevated
        label="Weekly"
        no-caps
        :text-color="
          scheduleFrequency === 'weekly' ? 'primary' : $q.dark.isActive ? 'white' : 'black'
        "
        @click="setDefaultSchedule('weekly')"
      />
      <q-btn
        unelevated
        label="Monthly"
        no-caps
        :text-color="
          scheduleFrequency === 'monthly' ? 'primary' : $q.dark.isActive ? 'white' : 'black'
        "
        @click="setDefaultSchedule('monthly')"
      />
    </q-btn-group>

    <div class="row q-col-gutter-sm" :style="{ width: '100%' }" :class="{ 'q-mt-sm': modelValue }">
      <q-select
        v-if="schedule?.dayOfMonth !== undefined"
        :modelValue="schedule.dayOfMonth"
        @update:model-value="schedule = { ...schedule, dayOfMonth: $event }"
        :options="dayOfMonthOptions"
        label="Day of Month"
        dense
        outlined
        emit-value
        map-options
        class="col"
      />
      <q-select
        v-if="schedule?.dayOfWeek !== undefined"
        :modelValue="schedule.dayOfWeek"
        @update:model-value="schedule = { ...schedule, dayOfWeek: $event }"
        :options="dayOfWeekOptions"
        multiple
        label="Day of Week"
        dense
        outlined
        emit-value
        map-options
        class="col"
      />
      <q-select
        v-if="schedule?.hour !== undefined"
        :modelValue="schedule.hour"
        @update:model-value="schedule = { ...schedule, hour: $event }"
        :options="hourOptions"
        label="Time (Central Time)"
        dense
        outlined
        emit-value
        map-options
        class="col"
      />
    </div>
    <div v-if="nextRunDate" class="q-mt-sm text-grey-7">
      <div>Next run: {{ nextRunDate.date }} ({{ nextRunDate.distance }} away)</div>
    </div>
  </div>
</template>

<script lang="ts">
import { type PropType } from 'vue'

import { formatDistance, format } from 'date-fns'
import cronParser, { type DayOfMonthRange, type DayOfWeekRange, type HourRange } from 'cron-parser'

// Custom type to represent the user's scheduling options.
type Schedule = {
  hour: HourRange
  dayOfWeek?: DayOfWeekRange[]
  dayOfMonth?: DayOfMonthRange
} | null

export default {
  name: 'ScheduleMaker',

  props: {
    // A cron expression string that we can manipulate.
    modelValue: {
      type: [String, null] as PropType<string | null>,
      required: true
    }
  },

  // Changes to the schedule are emitted as an updated cron expression
  emits: ['update:modelValue'],

  computed: {
    // This is our internal representation of the schedule. Cron expression
    // strings passed in are transformed into a Schedule and changes to this
    // schedule emit an updated version of the cron expression.
    schedule: {
      get() {
        return this.scheduleFromCron(this.modelValue ?? null)
      },
      set(newValue: Schedule) {
        this.$emit('update:modelValue', this.cronFromSchedule(newValue))
      }
    },
    nextRunDate() {
      const cron = this.modelValue
      if (!cron) {
        return null
      }

      // Parse cron expression as `America/Chicago` TZ.
      let nextDate
      try {
        nextDate = cronParser.parse(cron, { tz: 'America/Chicago' }).next().toDate()
      } catch {
        return null
      }

      // Convert to UTC to calculate the next run date.
      const now = new Date()
      const nowUtc = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds()
      )
      return {
        date: format(nextDate, 'EEE, MM/dd/yyyy'),
        distance: formatDistance(nextDate, nowUtc)
      }
    },
    scheduleFrequency(): 'daily' | 'weekly' | 'monthly' | null {
      if (!this.schedule) {
        return null
      }

      if (this.schedule.dayOfMonth !== undefined) {
        return 'monthly'
      } else if (this.schedule.dayOfWeek !== undefined) {
        return 'weekly'
      } else if (this.schedule.hour !== undefined) {
        return 'daily'
      }

      return null
    },
    hourOptions() {
      return Array.from({ length: 24 }, (_, i) => {
        const suffix = i >= 12 ? 'PM' : 'AM'
        const hour = i === 0 ? 12 : i // 0 is midnight; otherwise take the iterator
        const displayHour = hour > 12 ? hour - 12 : hour

        return {
          label: `${displayHour} ${suffix}`,
          value: i
        }
      })
    },
    dayOfWeekOptions() {
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(
        (day, index) => ({
          label: day,
          value: index
        })
      )
    },
    dayOfMonthOptions() {
      return Array.from({ length: 31 }, (_, i) => {
        const day = i + 1
        const lastDigit = day % 10
        const lastTwoDigits = day % 100
        let suffix = 'th'
        if (lastTwoDigits < 11 || lastTwoDigits > 13) {
          if (lastDigit === 1) suffix = 'st'
          else if (lastDigit === 2) suffix = 'nd'
          else if (lastDigit === 3) suffix = 'rd'
        }

        return {
          label: `${day}${suffix}`,
          value: day
        }
      })
    }
  },

  methods: {
    setDefaultSchedule(frequency: 'daily' | 'weekly' | 'monthly' | null) {
      switch (frequency) {
        case 'daily':
          this.schedule = { hour: 12 }
          break
        case 'weekly':
          this.schedule = { dayOfWeek: [1], hour: 12 }
          break
        case 'monthly':
          this.schedule = { dayOfMonth: 1, hour: 12 }
          break
        default:
          this.schedule = null
      }
    },

    cronFromSchedule(schedule: Schedule | null) {
      // If no saving state or schedule, return null
      if (schedule === null) {
        return null
      }

      const { hour } = schedule

      // For monthly schedule (includes first/last and day of week)
      if (schedule.dayOfMonth) {
        return `0 ${hour} ${schedule.dayOfMonth} * *`
      }

      // For weekly schedule (includes day of week)
      if (schedule.dayOfWeek && schedule.dayOfWeek.length > 0) {
        let dayOfWeek = schedule.dayOfWeek.join(',')
        return `0 ${hour} * * ${dayOfWeek}`
      }

      // We always have an hour in our schedule, so we can write the daily schedule.
      return `0 ${hour} * * *`
    },

    scheduleFromCron(cron: string | null): Schedule {
      if (cron === null || typeof cron !== 'string' || cron === '') {
        return null
      }

      // Use cronParser to extract the bits that we're interested in. Malformed
      // cron expressions will fail to parse and throw an error.
      let cronHour, cronMonth, cronWeek
      try {
        const { hour, dayOfMonth, dayOfWeek } = cronParser.parse(cron).fields
        cronHour = hour
        cronMonth = dayOfMonth
        cronWeek = dayOfWeek
      } catch (e) {
        console.error(e)
        console.error('Malformed Cron Expression: Cron expression failed to parse')
        return null
      }

      const hour = cronHour.values?.[0]
      const dayOfWeek = cronWeek.isWildcard ? undefined : (cronWeek.values ?? undefined)
      const dayOfMonth = cronMonth.isWildcard ? undefined : (cronMonth.values?.[0] ?? undefined)

      // Validate the schedule we're about to create adheres to our assumptions.
      if (hour === undefined) {
        console.error('Cron Expression must have an hour to create a Schedule.')
        return null
      }
      if (dayOfWeek !== undefined && dayOfMonth !== undefined) {
        console.error('Schedules do not currently support dayofWeek and dayOfMonth both being set.')
        return null
      }

      return { hour, dayOfWeek, dayOfMonth }
    }
  }
}
</script>
